import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {useMultiStyleConfig, defineStyle} from '@chakra-ui/system';
import {mode} from '@chakra-ui/theme-tools';

import {components as darkComponents} from '../../../design-tokens/dark';
import {components} from '../../../design-tokens/light';

const parts = [`root`, `item`, `ellipsis`, `prevTrigger`, `nextTrigger`, `icon`] as const;
export type PaginationParts = (typeof parts)[number];

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(parts);

const itemColorLight = components.colors.components.pagination[`text-default`];
const itemColorDark = darkComponents.colors.components.pagination[`text-default`];
const itemActiveColorLight = components.colors.components.pagination[`text-active`];
const itemActiveColorDark = darkComponents.colors.components.pagination[`text-active`];
const itemHoverColorLight = components.colors.components.pagination[`text-hover`];
const itemHoverColorDark = darkComponents.colors.components.pagination[`text-hover`];
const itemPressedColorLight = components.colors.components.pagination[`text-pressed`];
const itemPressedColorDark = darkComponents.colors.components.pagination[`text-pressed`];
const itemDisabledColorLight = components.colors.components.pagination[`text-disabled`];
const itemDisabledColorDark = darkComponents.colors.components.pagination[`text-disabled`];

const itemBgLight = components.colors.components.pagination[`background-default`];
const itemBgDark = darkComponents.colors.components.pagination[`background-default`];
const itemActiveBgLight = components.colors.components.pagination[`background-active`];
const itemActiveBgDark = darkComponents.colors.components.pagination[`background-active`];
const itemHoverBgLight = components.colors.components.pagination[`background-hover`];
const itemHoverBgDark = darkComponents.colors.components.pagination[`background-hover`];
const itemPressedBgLight = components.colors.components.pagination[`background-pressed`];
const itemPressedBgDark = darkComponents.colors.components.pagination[`background-pressed`];
const itemDisabledBgLight = components.colors.components.pagination[`background-disabled`];
const itemDisabledBgDark = darkComponents.colors.components.pagination[`background-disabled`];

const iconColorLight = components.colors.components.pagination[`icon-default`];
const iconColorDark = darkComponents.colors.components.pagination[`icon-default`];
const iconDisabledColorLight = components.colors.components.pagination[`icon-disabled`];
const iconDisabledColorDark = darkComponents.colors.components.pagination[`icon-disabled`];

const createItemStyles = (props: StyleFunctionProps) => {
  const colorDefault = mode(itemColorLight, itemColorDark)(props);
  const colorActive = mode(itemActiveColorLight, itemActiveColorDark)(props);
  const colorHover = mode(itemHoverColorLight, itemHoverColorDark)(props);
  const colorPressed = mode(itemPressedColorLight, itemPressedColorDark)(props);
  const colorDisabled = mode(itemDisabledColorLight, itemDisabledColorDark)(props);

  const bgDefault = mode(itemBgLight, itemBgDark)(props);
  const bgActive = mode(itemActiveBgLight, itemActiveBgDark)(props);
  const bgHover = mode(itemHoverBgLight, itemHoverBgDark)(props);
  const bgPressed = mode(itemPressedBgLight, itemPressedBgDark)(props);
  const bgDisabled = mode(itemDisabledBgLight, itemDisabledBgDark)(props);

  return defineStyle({
    color: colorDefault,
    bgColor: bgDefault,
    borderRadius: `radius-1000`,
    [`&[data-selected]`]: {
      color: colorActive,
      bgColor: bgActive,
    },
    [`&[disabled]`]: {
      color: colorDisabled,
      bgColor: bgDisabled,
    },
    '&:not([data-selected]):not([disabled]):hover': {
      color: colorHover,
      bgColor: bgHover,
    },
    '&:not([data-selected]):not([disabled]):active': {
      color: colorPressed,
      bgColor: bgPressed,
    },
  });
};

const paddingX = `space-150`;
const paddingY = `space-100`;

const root = defineStyle({
  fontSize: `sm`,
  display: `flex`,
  gap: `space-100`,
  '--pagination-skeleton-height': `24px`,
});

const item = defineStyle(props => {
  const itemStyles = createItemStyles(props);
  return {
    px: paddingX,
    py: paddingY,
    ...itemStyles,
  };
});

const icon = defineStyle(props => {
  const {disabled} = props;
  const colorDefault = mode(iconColorLight, iconColorDark)(props);
  const colorDisabled = mode(iconDisabledColorLight, iconDisabledColorDark)(props);
  return {
    color: disabled ? colorDisabled : colorDefault,
  };
});

const prevTrigger = defineStyle(props => {
  const itemStyles = createItemStyles(props);
  return {
    px: `space-100`,
    py: paddingY,
    ...itemStyles,
  };
});

const nextTrigger = defineStyle(props => {
  const itemStyles = createItemStyles(props);
  return {
    px: `space-100`,
    py: paddingY,
    ...itemStyles,
  };
});

const ellipsis = defineStyle({
  px: paddingX,
  py: paddingY,
});

const baseStyle = definePartsStyle(props => {
  return {
    root,
    item: item(props),
    prevTrigger: prevTrigger(props),
    nextTrigger: nextTrigger(props),
    ellipsis,
    icon: icon(props),
  };
});

const PaginationTheme = defineMultiStyleConfig({
  baseStyle,
});

export default PaginationTheme;

export const usePaginationThemeStyleConfig = <Part extends PaginationParts>(
  part: Part,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  // TODO(PRE-1122): rename PaginationTheme to Pagination after applying pagination to tables
  const pagination = useMultiStyleConfig(`PaginationTheme`, props ?? {});
  return pagination?.[part];
};
